/**
 * CryoVault Section
 *
 * A visually striking display of frozen eggs/embryos storage.
 * Features icy gradients, 3D assets, and floating orb visualizations.
 */

import {
  View,
  Pressable,
  ActivityIndicator,
  Image,
  Platform,
} from "react-native";
import { Text } from "@louise/ui/Text/Text";
import AnimatedRN, {
  FadeInDown,
  FadeIn,
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  Easing,
} from "react-native-reanimated";
import { useEffect, useMemo } from "react";
import { LinearGradient } from "expo-linear-gradient";
import Svg, { Defs, RadialGradient, Stop, Ellipse } from "react-native-svg";
import {
  MapPin,
  Calendar,
  ChevronRight,
  Plus,
  Thermometer,
} from "lucide-react-native";
import { useFrozenSpecimens } from "./useSpecimens";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import {
  parseNotesString,
  type ParsedNotesData,
} from "@src/packages/care/components/frozen-storage-data";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

// 3D snowflake asset
const snowflake3D = require("@assets/images/3d/snowflake.png");

export type StorageItem = {
  id: string;
  type: "eggs" | "embryos" | "sperm";
  count: number;
  facility: string;
  storedDate: string;
  cycleId?: string;
  notes: string[];
  collectedAt?: string;
  specimenIds: string[];
  storageLocationNotes?: string | null;
};

export type CryoVaultSectionProps = {
  onAddStorage?: () => void;
  onItemPress?: (item: StorageItem) => void;
  delay?: number;
};

function formatStoredDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  } catch {
    return dateString;
  }
}

// Data-driven gradient orb — colors shift based on egg/embryo ratio
// Inspired by the login screen's GradientOrb but with cryo-themed palette
function CryoVaultOrb({
  size = 180,
  eggCount,
  embryoCount,
}: {
  size?: number;
  eggCount: number;
  embryoCount: number;
}) {
  const total = eggCount + embryoCount;
  const eggW = total > 0 ? eggCount / total : 0.5;
  const embryoW = total > 0 ? embryoCount / total : 0.5;

  // Floor so the minority type still has some glow — higher floor = brighter
  const e = Math.max(0.3, eggW);
  const p = Math.max(0.3, embryoW);

  const rotation1 = useSharedValue(0);
  const rotation2 = useSharedValue(0);
  const rotation3 = useSharedValue(0);

  useEffect(() => {
    rotation1.value = withRepeat(
      withTiming(360, { duration: 14000, easing: Easing.linear }),
      -1,
      false,
    );
    rotation2.value = withRepeat(
      withTiming(-360, { duration: 20000, easing: Easing.linear }),
      -1,
      false,
    );
    rotation3.value = withRepeat(
      withTiming(360, { duration: 28000, easing: Easing.linear }),
      -1,
      false,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const l1 = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation1.value}deg` }],
  }));
  const l2 = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation2.value}deg` }],
  }));
  const l3 = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation3.value}deg` }],
  }));

  return (
    <View style={{ width: size, height: size }}>
      {/* Layer 1 — slow outer: cyan eggs + magenta embryos */}
      <AnimatedRN.View
        style={[
          {
            position: "absolute",
            width: size,
            height: size,
            // @ts-ignore – web filter
            filter: Platform.OS === "web" ? "blur(10px)" : undefined,
          },
          l1,
        ]}
      >
        <Svg width={size} height={size} viewBox="0 0 100 100">
          <Defs>
            <RadialGradient id="ce1" cx="22%" cy="28%" rx="42%" ry="48%">
              <Stop
                offset="0%"
                stopColor="#38BDF8"
                stopOpacity={String(Math.min(1, 1.1 * e))}
              />
              <Stop
                offset="40%"
                stopColor="#0EA5E9"
                stopOpacity={String(0.7 * e)}
              />
              <Stop offset="100%" stopColor="#0369A1" stopOpacity="0" />
            </RadialGradient>
            <RadialGradient id="cp1" cx="78%" cy="72%" rx="38%" ry="42%">
              <Stop
                offset="0%"
                stopColor="#F472B6"
                stopOpacity={String(Math.min(1, 1.05 * p))}
              />
              <Stop
                offset="45%"
                stopColor="#EC4899"
                stopOpacity={String(0.65 * p)}
              />
              <Stop offset="100%" stopColor="#BE185D" stopOpacity="0" />
            </RadialGradient>
          </Defs>
          <Ellipse cx="28" cy="32" rx="40" ry="44" fill="url(#ce1)" />
          <Ellipse cx="72" cy="68" rx="35" ry="38" fill="url(#cp1)" />
        </Svg>
      </AnimatedRN.View>

      {/* Layer 2 — counter-rotate: teal accent + violet accent */}
      <AnimatedRN.View
        style={[
          {
            position: "absolute",
            width: size,
            height: size,
            // @ts-ignore
            filter: Platform.OS === "web" ? "blur(6px)" : undefined,
          },
          l2,
        ]}
      >
        <Svg width={size} height={size} viewBox="0 0 100 100">
          <Defs>
            <RadialGradient id="ce2" cx="72%" cy="32%" rx="36%" ry="40%">
              <Stop
                offset="0%"
                stopColor="#67E8F9"
                stopOpacity={String(Math.min(1, 1.0 * e))}
              />
              <Stop
                offset="35%"
                stopColor="#22D3EE"
                stopOpacity={String(0.6 * e)}
              />
              <Stop offset="100%" stopColor="#0891B2" stopOpacity="0" />
            </RadialGradient>
            <RadialGradient id="cp2" cx="28%" cy="68%" rx="32%" ry="35%">
              <Stop
                offset="0%"
                stopColor="#C4B5FD"
                stopOpacity={String(0.9 * p)}
              />
              <Stop
                offset="50%"
                stopColor="#A78BFA"
                stopOpacity={String(0.5 * p)}
              />
              <Stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
            </RadialGradient>
          </Defs>
          <Ellipse cx="68" cy="35" rx="34" ry="38" fill="url(#ce2)" />
          <Ellipse cx="32" cy="65" rx="30" ry="32" fill="url(#cp2)" />
        </Svg>
      </AnimatedRN.View>

      {/* Layer 3 — fast inner: light cyan + coral pink */}
      <AnimatedRN.View
        style={[
          {
            position: "absolute",
            width: size,
            height: size,
            // @ts-ignore
            filter: Platform.OS === "web" ? "blur(4px)" : undefined,
          },
          l3,
        ]}
      >
        <Svg width={size} height={size} viewBox="0 0 100 100">
          <Defs>
            <RadialGradient id="ce3" cx="58%" cy="42%" rx="30%" ry="34%">
              <Stop
                offset="0%"
                stopColor="#BAE6FD"
                stopOpacity={String(0.9 * e)}
              />
              <Stop
                offset="40%"
                stopColor="#7DD3FC"
                stopOpacity={String(0.55 * e)}
              />
              <Stop offset="100%" stopColor="#38BDF8" stopOpacity="0" />
            </RadialGradient>
            <RadialGradient id="cp3" cx="42%" cy="58%" rx="26%" ry="30%">
              <Stop
                offset="0%"
                stopColor="#FBCFE8"
                stopOpacity={String(0.85 * p)}
              />
              <Stop
                offset="50%"
                stopColor="#F9A8D4"
                stopOpacity={String(0.5 * p)}
              />
              <Stop offset="100%" stopColor="#EC4899" stopOpacity="0" />
            </RadialGradient>
          </Defs>
          <Ellipse cx="56" cy="44" rx="28" ry="32" fill="url(#ce3)" />
          <Ellipse cx="44" cy="56" rx="24" ry="28" fill="url(#cp3)" />
        </Svg>
      </AnimatedRN.View>

      {/* Layer 4 — static hot center glow */}
      <View
        style={{
          position: "absolute",
          width: size,
          height: size,
          // @ts-ignore
          filter: Platform.OS === "web" ? "blur(6px)" : undefined,
        }}
      >
        <Svg width={size} height={size} viewBox="0 0 100 100">
          <Defs>
            <RadialGradient id="cc" cx="50%" cy="50%" rx="20%" ry="22%">
              <Stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
              <Stop offset="50%" stopColor="#F0F9FF" stopOpacity="0.55" />
              <Stop offset="100%" stopColor="#E0F2FE" stopOpacity="0" />
            </RadialGradient>
            <RadialGradient id="cw" cx="50%" cy="50%" rx="42%" ry="45%">
              <Stop offset="0%" stopColor="#BAE6FD" stopOpacity="0.35" />
              <Stop offset="60%" stopColor="#7DD3FC" stopOpacity="0.15" />
              <Stop offset="100%" stopColor="#38BDF8" stopOpacity="0" />
            </RadialGradient>
          </Defs>
          <Ellipse cx="50" cy="50" rx="40" ry="42" fill="url(#cw)" />
          <Ellipse cx="50" cy="50" rx="18" ry="20" fill="url(#cc)" />
        </Svg>
      </View>

      {/* Grain texture overlay — web only */}
      {Platform.OS === "web" && (
        <View
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: size / 2,
            opacity: 0.3,
            // @ts-ignore
            mixBlendMode: "overlay",
            // @ts-ignore
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            backgroundSize: "60px 60px",
          }}
          pointerEvents="none"
        />
      )}
    </View>
  );
}

// Build status pills from parsed notes data
function getStatusPills(
  parsed: ParsedNotesData,
  type: "eggs" | "embryos" | "sperm",
): string[] {
  const pills: string[] = [];
  if (parsed.freezingMethod) {
    const label =
      parsed.freezingMethod === "vitrification"
        ? "Vitrification"
        : parsed.freezingMethod === "slow_freeze"
          ? "Slow Freeze"
          : "";
    if (label) pills.push(label);
  }
  if (type === "embryos") {
    if (parsed.dayOfDevelopment) {
      const day = parsed.dayOfDevelopment;
      pills.push(day === "5" || day === "6" ? `Day ${day}` : `Day ${day}`);
    }
    if (parsed.pgtPerformed && parsed.pgtPerformed !== "not_performed") {
      const pgtLabels: Record<string, string> = {
        pgt_a: "PGT-A",
        pgt_m: "PGT-M",
        pgt_sr: "PGT-SR",
        pgt_a_m: "PGT-A+M",
        pgt_p: "PGT-P",
        pending: "PGT Pending",
      };
      pills.push(pgtLabels[parsed.pgtPerformed] ?? "PGT");
    }
    if (parsed.fertilisationMethod) {
      const method = parsed.fertilisationMethod;
      if (method === "icsi") pills.push("ICSI");
      else if (method === "ivf") pills.push("IVF");
      else if (method === "split") pills.push("Split");
    }
  }
  return pills;
}

// Rich storage item card
// Single glowing gradient orb with blur + grain — matches hero orb aesthetic
function TypeOrb({
  color,
  size = 38,
}: {
  color: "egg" | "embryo" | "sperm";
  size?: number;
}) {
  const c1 =
    color === "egg" ? "#38BDF8" : color === "sperm" ? "#A78BFA" : "#F472B6";
  const cLight =
    color === "egg" ? "#BAE6FD" : color === "sperm" ? "#DDD6FE" : "#FBCFE8";
  const cMid =
    color === "egg" ? "#0EA5E9" : color === "sperm" ? "#8B5CF6" : "#EC4899";
  const id = color === "egg" ? "to_e" : color === "sperm" ? "to_s" : "to_p";

  return (
    <View style={{ width: size, height: size }}>
      {/* Single blurred orb */}
      <View
        style={{
          position: "absolute",
          width: size,
          height: size,
          // @ts-ignore
          filter: Platform.OS === "web" ? "blur(3px)" : undefined,
        }}
      >
        <Svg width={size} height={size} viewBox="0 0 40 40">
          <Defs>
            <RadialGradient id={`${id}b`} cx="44%" cy="42%" rx="38%" ry="38%">
              <Stop offset="0%" stopColor={cLight} stopOpacity="1" />
              <Stop offset="45%" stopColor={c1} stopOpacity="0.9" />
              <Stop offset="80%" stopColor={cMid} stopOpacity="0.6" />
              <Stop offset="100%" stopColor={cMid} stopOpacity="0" />
            </RadialGradient>
            <RadialGradient id={`${id}s`} cx="38%" cy="34%" rx="22%" ry="22%">
              <Stop offset="0%" stopColor="#ffffff" stopOpacity="0.8" />
              <Stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </RadialGradient>
          </Defs>
          {/* Main sphere */}
          <Ellipse cx="20" cy="20" rx="15" ry="15" fill={`url(#${id}b)`} />
          {/* Specular highlight */}
          <Ellipse cx="16" cy="16" rx="7" ry="7" fill={`url(#${id}s)`} />
        </Svg>
      </View>

      {/* Grain overlay */}
      {Platform.OS === "web" && (
        <View
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: size / 2,
            opacity: 0.3,
            // @ts-ignore
            mixBlendMode: "overlay",
            // @ts-ignore
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            backgroundSize: "40px 40px",
          }}
          pointerEvents="none"
        />
      )}
    </View>
  );
}

function StorageItemCard({
  item,
  index,
  delay,
  onPress,
}: {
  item: StorageItem;
  index: number;
  delay: number;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("StorageItemCard");
  const typeColor =
    item.type === "eggs"
      ? "#38BDF8"
      : item.type === "sperm"
        ? "#A78BFA"
        : "#F472B6";
  const parsed = useMemo(() => {
    if (item.notes.length > 0) {
      return parseNotesString(item.notes[0]!);
    }
    return {} as ParsedNotesData;
  }, [item.notes]);

  const clinicName = parsed.clinicName || item.facility;
  const pills = useMemo(
    () => getStatusPills(parsed, item.type),
    [parsed, item.type],
  );

  return (
    <AnimatedRN.View entering={FadeIn.delay(delay + 100 + index * 80)}>
      <Pressable
        onPress={() => {
          capturePress(pressLabel(clinicName), "storage_item_card");
          onPress();
        }}
        style={({ pressed }) => ({
          overflow: "hidden",
          borderRadius: 16,
          backgroundColor: pressed
            ? "rgba(255, 255, 255, 0.06)"
            : "rgba(255, 255, 255, 0.02)",
          borderWidth: 1,
          borderColor: pressed ? `${typeColor}45` : `${typeColor}18`,
          shadowColor: typeColor,
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: pressed ? 0.3 : 0.12,
          shadowRadius: 20,
        })}
      >
        {/* Inner glow — gradient from type color on the left */}
        <LinearGradient
          colors={[`${typeColor}12`, "transparent"]}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 0.5, y: 0.5 }}
          style={{ position: "absolute", top: 0, left: 0, bottom: 0, right: 0 }}
        />

        <View style={{ padding: 14 }}>
          {/* Main row: icon | info | chevron */}
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            {/* 3D icon with count badge */}
            <View style={{ position: "relative", marginRight: 10 }}>
              <TypeOrb
                color={
                  item.type === "eggs"
                    ? "egg"
                    : item.type === "sperm"
                      ? "sperm"
                      : "embryo"
                }
                size={38}
              />
              {/* Count badge */}
              <View
                style={{
                  position: "absolute",
                  bottom: -2,
                  right: -4,
                  backgroundColor: typeColor,
                  borderRadius: 8,
                  minWidth: 18,
                  height: 18,
                  alignItems: "center",
                  justifyContent: "center",
                  paddingHorizontal: 4,
                  shadowColor: typeColor,
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: 0.5,
                  shadowRadius: 6,
                }}
              >
                <Text
                  style={{
                    fontSize: 10,
                    fontWeight: "700",
                    color: "#020617",
                  }}
                >
                  {item.count}
                </Text>
              </View>
            </View>

            {/* Clinic name + metadata stacked */}
            <View style={{ flex: 1 }}>
              <Text
                numberOfLines={1}
                style={{
                  fontSize: 14,
                  fontWeight: "600",
                  color: "rgba(255, 255, 255, 0.9)",
                  letterSpacing: -0.2,
                }}
              >
                {clinicName}
              </Text>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 4,
                }}
              >
                <View
                  style={{ flexDirection: "row", alignItems: "center", gap: 3 }}
                >
                  <MapPin size={9} color="rgba(255, 255, 255, 0.35)" />
                  <Text
                    numberOfLines={1}
                    style={{ fontSize: 10, color: "rgba(255, 255, 255, 0.4)" }}
                  >
                    {item.facility}
                  </Text>
                </View>
                <View
                  style={{ flexDirection: "row", alignItems: "center", gap: 3 }}
                >
                  <Calendar size={9} color="rgba(255, 255, 255, 0.35)" />
                  <Text
                    style={{ fontSize: 10, color: "rgba(255, 255, 255, 0.4)" }}
                  >
                    {item.storedDate}
                  </Text>
                </View>
              </View>
            </View>

            <ChevronRight size={16} color="rgba(255, 255, 255, 0.25)" />
          </View>

          {/* Status pills */}
          {pills.length > 0 && (
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 5,
                marginTop: 10,
                marginLeft: 48,
              }}
            >
              {pills.map((pill) => (
                <View
                  key={pill}
                  style={{
                    paddingHorizontal: 8,
                    paddingVertical: 3,
                    borderRadius: 8,
                    backgroundColor: `${typeColor}12`,
                    borderWidth: 1,
                    borderColor: `${typeColor}22`,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 9,
                      fontWeight: "600",
                      color: typeColor,
                      letterSpacing: 0.3,
                      opacity: 0.85,
                    }}
                  >
                    {pill}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </View>
      </Pressable>
    </AnimatedRN.View>
  );
}

export function CryoVaultSection({
  onAddStorage,
  onItemPress,
  delay = 400,
}: CryoVaultSectionProps) {
  const capturePress = usePressCapture("CryoVaultSection");
  const {
    storageGroups,
    totalEggs,
    totalEmbryos,
    totalSperm,
    isLoading,
    error,
  } = useFrozenSpecimens();
  const { data: patient } = usePatient();
  const gender: "male" | "female" | null =
    patient?.gender === "AMAB"
      ? "male"
      : patient?.gender === "AFAB"
        ? "female"
        : null;

  const items: StorageItem[] = storageGroups.map((group, index) => ({
    id: `${group.type}-${group.facility}-${index}`,
    type:
      group.type === "oocyte"
        ? "eggs"
        : group.type === "semen"
          ? "sperm"
          : "embryos",
    count: group.specimens.length,
    facility: group.facility,
    storedDate: formatStoredDate(group.storedDate),
    notes: group.notes,
    collectedAt: group.collectedAt ?? undefined,
    specimenIds: group.specimenIds,
    storageLocationNotes: group.storageLocationNotes,
  }));

  const hasItems = items.length > 0;
  const totalFrozen = totalEggs + totalEmbryos + totalSperm;

  // Gender-gated pills:
  // - female: eggs + embryos
  // - male: sperm + embryos
  // - unknown: show all non-zero (don't drop data)
  const showEggsPill =
    gender === "female" || (gender === null && totalEggs > 0);
  const showSpermPill =
    gender === "male" || (gender === null && totalSperm > 0);

  return (
    <AnimatedRN.View
      entering={FadeInDown.delay(delay).springify()}
      style={{ marginBottom: 24 }}
    >
      {/* Section Header */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 12,
          paddingHorizontal: 4,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <Image
            source={snowflake3D}
            style={{ width: 24, height: 24 }}
            resizeMode="contain"
          />
          <View>
            <Text
              style={{
                fontSize: 20,
                fontFamily: "Tobias-Thin",
                color: "#ffffff",
              }}
            >
              CryoVault
            </Text>
            <Text
              style={{
                fontSize: 13,
                color: "#94a3b8",
                lineHeight: 20,
                marginTop: 4,
              }}
            >
              Track and manage your frozen eggs and embryos.
            </Text>
          </View>
        </View>
        {hasItems && (
          <Pressable
            onPress={() => {
              capturePress("Add", "add_frozen_storage");
              onAddStorage?.();
            }}
            style={({ pressed }) => ({
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
              paddingHorizontal: 10,
              paddingVertical: 6,
              borderRadius: 12,
              backgroundColor: pressed
                ? "rgba(56, 189, 248, 0.15)"
                : "rgba(56, 189, 248, 0.08)",
              opacity: pressed ? 0.8 : 1,
            })}
          >
            <Plus size={14} color="#38BDF8" />
            <Text style={{ fontSize: 12, color: "#38BDF8", fontWeight: "500" }}>
              Add
            </Text>
          </Pressable>
        )}
      </View>

      {/* Main Card with icy gradient */}
      <View
        style={{
          borderRadius: 20,
          overflow: "hidden",
          borderWidth: 1,
          borderColor: "rgba(56, 189, 248, 0.2)",
        }}
      >
        <LinearGradient
          colors={[
            "rgba(15, 23, 42, 0.95)",
            "rgba(30, 58, 95, 0.85)",
            "rgba(15, 23, 42, 0.95)",
          ]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ minHeight: 100 }}
        >
          {/* Frost overlay effect */}
          <View
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.3,
            }}
          >
            <LinearGradient
              colors={[
                "rgba(56, 189, 248, 0.1)",
                "transparent",
                "rgba(244, 114, 182, 0.05)",
              ]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{ flex: 1 }}
            />
          </View>

          {/* Loading State */}
          {isLoading && (
            <View style={{ padding: 40, alignItems: "center" }}>
              <ActivityIndicator size="small" color="#38BDF8" />
              <Text
                style={{
                  fontSize: 12,
                  color: "rgba(255, 255, 255, 0.6)",
                  marginTop: 8,
                }}
              >
                Loading vault...
              </Text>
            </View>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <View style={{ padding: 32, alignItems: "center" }}>
              <Text style={{ fontSize: 13, color: "#EF4444" }}>
                Unable to load storage data
              </Text>
            </View>
          )}

          {/* Content */}
          {!isLoading && !error && (
            <>
              {hasItems ? (
                <>
                  {/* Hero Section — orb as centerpiece */}
                  <View
                    style={{
                      paddingHorizontal: 20,
                      paddingTop: 16,
                      paddingBottom: 20,
                      alignItems: "center",
                    }}
                  >
                    {/* Top row: breakdown pills left, temp badge right */}
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        marginBottom: 8,
                      }}
                    >
                      {/* Temperature badge — top left */}
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 4,
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 8,
                          backgroundColor: "rgba(56, 189, 248, 0.1)",
                        }}
                      >
                        <Thermometer size={12} color="#38BDF8" />
                        <Text
                          style={{
                            fontSize: 10,
                            color: "#38BDF8",
                            fontWeight: "600",
                          }}
                        >
                          -196°C
                        </Text>
                      </View>

                      {/* Breakdown pills — top right */}
                      <View style={{ flexDirection: "row", gap: 6 }}>
                        {showEggsPill && totalEggs > 0 && (
                          <View
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 4,
                              paddingHorizontal: 8,
                              paddingVertical: 4,
                              borderRadius: 20,
                              backgroundColor: "rgba(56, 189, 248, 0.1)",
                            }}
                          >
                            <View
                              style={{
                                width: 5,
                                height: 5,
                                borderRadius: 2.5,
                                backgroundColor: "#38BDF8",
                                shadowColor: "#38BDF8",
                                shadowOffset: { width: 0, height: 0 },
                                shadowOpacity: 0.6,
                                shadowRadius: 4,
                              }}
                            />
                            <Text
                              style={{
                                fontSize: 11,
                                fontWeight: "500",
                                color: "rgba(255, 255, 255, 0.65)",
                              }}
                            >
                              {totalEggs} {totalEggs === 1 ? "egg" : "eggs"}
                            </Text>
                          </View>
                        )}
                        {showSpermPill && totalSperm > 0 && (
                          <View
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 4,
                              paddingHorizontal: 8,
                              paddingVertical: 4,
                              borderRadius: 20,
                              backgroundColor: "rgba(167, 139, 250, 0.1)",
                            }}
                          >
                            <View
                              style={{
                                width: 5,
                                height: 5,
                                borderRadius: 2.5,
                                backgroundColor: "#A78BFA",
                                shadowColor: "#A78BFA",
                                shadowOffset: { width: 0, height: 0 },
                                shadowOpacity: 0.6,
                                shadowRadius: 4,
                              }}
                            />
                            <Text
                              style={{
                                fontSize: 11,
                                fontWeight: "500",
                                color: "rgba(255, 255, 255, 0.65)",
                              }}
                            >
                              {totalSperm} {totalSperm === 1 ? "vial" : "vials"}
                            </Text>
                          </View>
                        )}
                        {totalEmbryos > 0 && (
                          <View
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 4,
                              paddingHorizontal: 8,
                              paddingVertical: 4,
                              borderRadius: 20,
                              backgroundColor: "rgba(244, 114, 182, 0.1)",
                            }}
                          >
                            <View
                              style={{
                                width: 5,
                                height: 5,
                                borderRadius: 2.5,
                                backgroundColor: "#F472B6",
                                shadowColor: "#F472B6",
                                shadowOffset: { width: 0, height: 0 },
                                shadowOpacity: 0.6,
                                shadowRadius: 4,
                              }}
                            />
                            <Text
                              style={{
                                fontSize: 11,
                                fontWeight: "500",
                                color: "rgba(255, 255, 255, 0.65)",
                              }}
                            >
                              {totalEmbryos}{" "}
                              {totalEmbryos === 1 ? "embryo" : "embryos"}
                            </Text>
                          </View>
                        )}
                      </View>
                    </View>

                    {/* The orb — smaller, centered, with stats overlaid */}
                    <View
                      style={{
                        width: 140,
                        height: 140,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CryoVaultOrb
                        size={140}
                        eggCount={gender === "male" ? totalSperm : totalEggs}
                        embryoCount={totalEmbryos}
                      />
                      {/* Stats floating on the orb */}
                      <View
                        style={{
                          position: "absolute",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 44,
                            fontWeight: "200",
                            color: "#ffffff",
                            fontFamily: "Tobias-Thin",
                            letterSpacing: -1.5,
                            textAlign: "center",
                            lineHeight: 48,
                            textShadowColor: "rgba(0, 0, 0, 0.5)",
                            textShadowOffset: { width: 0, height: 1 },
                            textShadowRadius: 8,
                          }}
                        >
                          {totalFrozen}
                        </Text>
                        <Text
                          style={{
                            fontSize: 9,
                            color: "rgba(255, 255, 255, 0.6)",
                            textAlign: "center",
                            marginTop: 3,
                            textTransform: "uppercase",
                            letterSpacing: 2.5,
                            textShadowColor: "rgba(0, 0, 0, 0.4)",
                            textShadowOffset: { width: 0, height: 1 },
                            textShadowRadius: 4,
                          }}
                        >
                          {totalFrozen === 1 ? "specimen" : "specimens"}
                        </Text>
                      </View>
                    </View>
                  </View>

                  {/* Storage Locations */}
                  <View
                    style={{
                      padding: 12,
                      gap: 10,
                      borderTopWidth: 1,
                      borderTopColor: "rgba(255, 255, 255, 0.05)",
                    }}
                  >
                    {items.map((item, index) => (
                      <StorageItemCard
                        key={item.id}
                        item={item}
                        index={index}
                        delay={delay}
                        onPress={() => onItemPress?.(item)}
                      />
                    ))}
                  </View>

                  {/* Storage info & annual cost awareness */}
                  <View
                    style={{
                      paddingHorizontal: 16,
                      paddingBottom: 16,
                      gap: 8,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 11,
                        color: "rgba(255, 255, 255, 0.5)",
                        lineHeight: 16,
                      }}
                    >
                      Vitrified specimens maintain their quality indefinitely at
                      -196°C.
                    </Text>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 8,
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        borderRadius: 10,
                        backgroundColor: "rgba(251, 191, 36, 0.08)",
                        borderWidth: 1,
                        borderColor: "rgba(251, 191, 36, 0.15)",
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 11,
                          color: "#FBBF24",
                          flex: 1,
                          lineHeight: 16,
                        }}
                      >
                        Annual storage: ~$500{"\u2013"}1,000/year. Check with
                        your facility for renewal dates.
                      </Text>
                    </View>
                  </View>
                </>
              ) : (
                /* Empty State */
                <Pressable
                  onPress={() => {
                    capturePress("Add your first cycle", "add_first_cycle");
                    onAddStorage?.();
                  }}
                  style={({ pressed }) => ({
                    padding: 32,
                    alignItems: "center",
                    backgroundColor: pressed
                      ? "rgba(255, 255, 255, 0.02)"
                      : "transparent",
                  })}
                >
                  <View style={{ position: "relative", marginBottom: 16 }}>
                    <Image
                      source={snowflake3D}
                      style={{ width: 64, height: 64, opacity: 0.9 }}
                      resizeMode="contain"
                    />
                    {/* Glow effect */}
                    <View
                      style={{
                        position: "absolute",
                        top: -10,
                        left: -10,
                        right: -10,
                        bottom: -10,
                        borderRadius: 50,
                        backgroundColor: "rgba(56, 189, 248, 0.1)",
                      }}
                    />
                  </View>

                  <Text
                    style={{
                      fontSize: 16,
                      fontWeight: "500",
                      color: "rgba(255, 255, 255, 0.9)",
                      marginBottom: 6,
                    }}
                  >
                    Your vault is empty
                  </Text>
                  <Text
                    style={{
                      fontSize: 13,
                      color: "rgba(255, 255, 255, 0.6)",
                      textAlign: "center",
                      maxWidth: 260,
                      lineHeight: 20,
                    }}
                  >
                    Track your frozen eggs and embryos in one place
                  </Text>

                  {/* Add Button */}
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 6,
                      marginTop: 20,
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 24,
                      backgroundColor: "#38BDF8",
                      shadowColor: "#38BDF8",
                      shadowOffset: { width: 0, height: 4 },
                      shadowOpacity: 0.3,
                      shadowRadius: 12,
                    }}
                  >
                    <Plus size={16} color="#020617" />
                    <Text
                      style={{
                        fontSize: 14,
                        color: "#020617",
                        fontWeight: "600",
                      }}
                    >
                      Add your first cycle
                    </Text>
                  </View>
                </Pressable>
              )}
            </>
          )}
        </LinearGradient>
      </View>
    </AnimatedRN.View>
  );
}
